import { VALUE_DIFFERENCE_DISPLAY } from 'components/View/constants';
import { COMPANY_COMMUNITY_RELATION_TYPE } from 'constants/company';
import { toNumber } from 'utils/helpers/numbers';

import { WIDGET_TYPES } from './constants';
import { valueFormatter } from './DataTable/utils';
import { WidgetDataPoint } from './types';
import { DATA_RANGE_TYPE } from './Widget/constants';

const DEFAULT_KEY = 'default';

const WIDGET_PRECISION: Record<number, number> = {
  [WIDGET_TYPES.RATING]: 1,
  [WIDGET_TYPES.RATING_GRAPH]: 1,
  [WIDGET_TYPES.MARKET_RATE_SQUARE_FOOTAGE]: 2,
  [WIDGET_TYPES.MARKET_RATE_SQUARE_FOOTAGE_GRAPH]: 2,
  [WIDGET_TYPES.SWOT_SCORE]: 2,
};

const roundToPrecision = (value: number, decimals: number) => {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
};

const calculatePercentageDifference = (baseValue: number | null, targetValue: number | null, widgetType?: number) => {
  if (baseValue === null || baseValue === 0 || targetValue === null) {
    return null;
  }

  // Round values to match displayed values before calculating percentage
  const precision = widgetType !== undefined ? WIDGET_PRECISION[widgetType] ?? 0 : 0;
  const roundedBase = roundToPrecision(baseValue, precision);
  const roundedTarget = roundToPrecision(targetValue, precision);

  if (roundedBase === 0) {
    return null;
  }

  const percentage = ((roundedTarget - roundedBase) / roundedBase) * 100;
  return Math.round(percentage);
};

export const shouldDisplayValueDifference = (comparisonTypeEnabled?: boolean, valueDisplay?: number): boolean =>
  comparisonTypeEnabled === true && valueDisplay !== VALUE_DIFFERENCE_DISPLAY.DONT_DISPLAY;

export const calculateValueDifference = (
  baseValue: number | null,
  targetValue: number | null,
  valueDisplay?: number,
  widgetType?: number
) => {
  if (baseValue === null || targetValue === null) {
    return null;
  }

  if (valueDisplay === VALUE_DIFFERENCE_DISPLAY.AMOUNTS) {
    const precision = widgetType !== undefined ? WIDGET_PRECISION[widgetType] ?? 0 : 0;
    const roundedBase = roundToPrecision(baseValue, precision);
    const roundedTarget = roundToPrecision(targetValue, precision);
    return roundToPrecision(roundedTarget - roundedBase, precision);
  }

  if (valueDisplay === VALUE_DIFFERENCE_DISPLAY.PERCENTAGES) {
    return calculatePercentageDifference(baseValue, targetValue, widgetType);
  }

  return null;
};

export const getDifferenceColor = (difference: number, widgetType?: number) => {
  const competitorOutperformsCommunity = difference > 0;

  if (widgetType === WIDGET_TYPES.SWOT_SCORE) {
    return competitorOutperformsCommunity ? 'var(--negative-orange)' : 'var(--positive-green)';
  }

  return difference < 0 ? 'var(--negative-orange)' : 'var(--positive-green)';
};

export const formatValueDifference = (
  difference: number | null | undefined,
  widgetType: number,
  valueDisplay?: number,
  unitSource?: any
) => {
  if (difference === null || difference === undefined || difference === 0) {
    return null;
  }

  if (valueDisplay === VALUE_DIFFERENCE_DISPLAY.AMOUNTS) {
    const formattedValue = valueFormatter(Math.abs(difference), widgetType, true, 0, unitSource);
    if (!formattedValue) {
      return null;
    }
    const sign = difference > 0 ? '+' : '-';
    return `${sign}${formattedValue}`;
  }

  if (valueDisplay === VALUE_DIFFERENCE_DISPLAY.PERCENTAGES) {
    const sign = difference > 0 ? '+' : '-';
    return `${sign}${Math.abs(difference).toLocaleString()}%`;
  }

  return null;
};

const getDataPointKey = (item: WidgetDataPoint, rangeType: string) => {
  if (rangeType === DATA_RANGE_TYPE.OVERTIME) {
    return item.quarterDate ?? item.date ?? DEFAULT_KEY;
  }

  if (rangeType === DATA_RANGE_TYPE.GROUPED) {
    return item.group_by ?? DEFAULT_KEY;
  }

  return DEFAULT_KEY;
};

const isCommunityDataPoint = (item: WidgetDataPoint, communityId?: number) => {
  if (!communityId) {
    return item.community_type === COMPANY_COMMUNITY_RELATION_TYPE.COMMUNITY;
  }

  return item.community_id === communityId;
};

const isCompetitorDataPoint = (item: WidgetDataPoint, communityId?: number) => {
  if (!communityId) {
    return item.community_type === COMPANY_COMMUNITY_RELATION_TYPE.COMPETITOR;
  }

  return item.community_id !== communityId;
};

export const applyValueDifferenceToWidgetData = <T extends WidgetDataPoint>(
  data: T[] | undefined,
  options: {
    rangeType: string;
    widgetType: number;
    communityId?: number;
    comparisonTypeEnabled?: boolean;
    valueDisplay?: number;
  }
) => {
  if (!Array.isArray(data) || !shouldDisplayValueDifference(options.comparisonTypeEnabled, options.valueDisplay)) {
    return data;
  }

  const baseValues = new Map<string, number>();

  data.forEach((item) => {
    if (!isCommunityDataPoint(item, options.communityId)) {
      return;
    }
    const key = getDataPointKey(item, options.rangeType);
    const numericValue = toNumber(item.value);
    if (numericValue === null) {
      return;
    }
    baseValues.set(key, numericValue);
  });

  return data.map((item) => {
    if (!isCompetitorDataPoint(item, options.communityId)) {
      if (item.difference !== undefined) {
        const { difference, ...rest } = item;
        return rest as T;
      }
      return item;
    }

    const key = getDataPointKey(item, options.rangeType);
    const baseValue = baseValues.get(key);
    const targetValue = toNumber(item.value);
    if (baseValue === undefined || targetValue === null) {
      if (item.difference !== undefined) {
        const { difference, ...rest } = item;
        return rest as T;
      }
      return item;
    }

    const difference = calculateValueDifference(baseValue, targetValue, options.valueDisplay, options.widgetType);

    if (difference === null || difference === 0) {
      if (item.difference !== undefined) {
        const { difference: itemDifference, ...rest } = item;
        return rest as T;
      }
      return item;
    }

    return {
      ...item,
      difference,
    };
  });
};
